import {
  UNAVAILABLE_LABELS,
  UNAVAILABLE_STATES,
  unavailableTotal,
  type InventoryRow,
} from "@estelle/domain";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Pencil } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * The figure cells of Stock Levels (#602 part C), mirroring what Shopify's own
 * inventory screen shows for a location: Unavailable with its breakdown, and
 * Available and On hand with a quick adjust. In our own styling and inside the
 * shared table.
 */

/** A number, red when it is below zero: negative stock is wrong, not just low. */
export function Figure({
  value,
  className,
}: {
  value: number;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "tabular-nums",
        value < 0 && "text-destructive font-semibold",
        className,
      )}
    >
      {value}
    </span>
  );
}

/** Unavailable, with a small chevron that opens damaged / quality control / safety stock / reserved. */
export function UnavailableCell({ row }: { row: InventoryRow }) {
  const total = unavailableTotal(row.unavailable);
  if (total === 0) return <Figure value={0} />;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Unavailable ${String(total)}, show the breakdown`}
          className="hover:bg-accent inline-flex items-center gap-0.5 rounded px-1 tabular-nums"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {total}
          <ChevronDown aria-hidden className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-56 p-3"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <p className="mb-1.5 text-sm font-medium">Unavailable</p>
        <dl className="space-y-1 text-sm">
          {UNAVAILABLE_STATES.map((state) => (
            <div key={state} className="flex justify-between">
              <dt className="text-muted-foreground">
                {UNAVAILABLE_LABELS[state]}
              </dt>
              <dd className="tabular-nums">{row.unavailable[state]}</dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
}

/** In transit, saying what it is: ours, from open transfers, not Shopify's incoming. */
export function InTransitCell({ row }: { row: InventoryRow }) {
  return (
    <span
      className="tabular-nums"
      title="In transit: stock dispatched here by open stock transfers and not yet received. Counted by this ERP, not Shopify's incoming."
    >
      {row.inTransit}
    </span>
  );
}

const newApplyId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `apply-${String(Date.now())}-${String(Math.random()).slice(2, 10)}`;

/**
 * Available or On hand, with a pencil for someone who may change stock. It sets
 * the figure through the Update stock write (Shopify accepts first, then the
 * ERP), so a correction here is the same recorded, reasoned change as one made
 * on that screen.
 */
export function AdjustableFigure({
  row,
  figure,
}: {
  row: InventoryRow;
  figure: "available" | "onHand";
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const current = figure === "available" ? row.available : row.onHand;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(String(current));
  const [problem, setProblem] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const target = Number(value);
      // Available is set as it is; On hand moves available by the difference.
      const delta = target - current;
      return data.inventory.applyStockUpdate({
        warehouseId: row.warehouseId,
        reason: "correction",
        applyId: newApplyId(),
        lines: [
          figure === "available"
            ? { variantId: row.variantId, mode: "set", quantity: target }
            : {
                variantId: row.variantId,
                mode: delta >= 0 ? "add" : "remove",
                quantity: Math.abs(delta),
              },
        ],
      });
    },
    onSuccess: (result) => {
      if (!result.ok) {
        setProblem(result.rejection.message);
        return;
      }
      const line = result.data.lines[0];
      if (line?.status === "rejected") {
        setProblem(line.message ?? "Refused.");
        return;
      }
      setProblem(null);
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });

  const target = Number(value);
  const valid = value.trim() !== "" && Number.isInteger(target) && target >= 0;

  if (!can(permissions, "inventory.adjustment.create")) {
    return <Figure value={current} />;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <Figure value={current} />
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) {
            setValue(String(current));
            setProblem(null);
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Change ${figure === "available" ? "available" : "on hand"} for ${row.variant.sku ?? row.product.title}`}
            className="text-muted-foreground hover:text-foreground rounded p-0.5"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Pencil aria-hidden className="size-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-64 space-y-2 p-3"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (valid && target !== current) mutation.mutate();
            }}
          >
            <p className="text-sm font-medium">
              Set {figure === "available" ? "available" : "on hand"} to
            </p>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              autoFocus
              value={value}
              aria-label="New number"
              onChange={(event) => {
                setValue(event.target.value);
              }}
            />
            <p className="text-muted-foreground text-xs">
              Recorded as a correction, and sent to Shopify first.
            </p>
            {problem ? (
              <p role="alert" className="text-destructive text-xs">
                {problem}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!valid || target === current || mutation.isPending}
              >
                {mutation.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>
    </span>
  );
}
